import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { generatePath, Link, Redirect } from 'react-router-dom';
import {
  isError, isFinishedUpdating, isUpdating, ResourcesItem,
} from 'redux-and-the-rest';

import { PasswordResetModel } from '../../models/passwordResetModel';
import {
  clearPasswordReset,
  validateToken,
  getPasswordReset,
  updatePassword,
} from '../../redux/reducers/passwordResetReducer';
import { ApplicationState } from '../../redux/types';
import { Routes } from '../../routes';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../utils/FormUtils';
import Header from '../shared/Header';
import TextInput from '../shared/TextInput';

interface ResetPasswordFields {
  password: string;
  confirmPassword: string;
}

interface Props {
  onSubmit(password: string, token: string): void;
  passwordReset: ResourcesItem<PasswordResetModel>
  onValidateToken(token: string): void;
  resetToken: string;
}

const ResetPasswordPanel = ({
  onSubmit, passwordReset, onValidateToken, resetToken,
}: Props) => {
  const {
    register, handleSubmit, formState: { errors }, getValues, setError,
  } = useForm<ResetPasswordFields>();

  const submit = handleSubmit(({ password }) => {
    onSubmit(password, resetToken);
  });

  const isUpdatingPassword = isUpdating(passwordReset);
  const [tokenError, setTokenError] = useState(false);
  const [requireFreshLogin, setRequireFreshLogin] = useState(false);

  // Kick off a fetch for the token to check current validity
  useEffect(() => {
    onValidateToken(resetToken);
  }, [resetToken]);

  useEffect(() => {
    if (isError(passwordReset)) {
      const { httpCode } = passwordReset.status;

      /**
       * We can only have one of two errors identifiable by the httpCode returned
       * 404: the token is invalid
       * 422: the form value, ie password, is invalid
       */
      if (httpCode === 404) {
        clearPasswordReset();
        setTokenError(true);
      } else if (httpCode === 422) {
        setError('password', { message: `A password of at least ${PASSWORD_MIN_LENGTH} characters is required` });
      }
    } else if (isFinishedUpdating(passwordReset)) {
      clearPasswordReset();
      setRequireFreshLogin(true);
    }
  }, [passwordReset.status]);

  /**
   * Once the password is reset, we redirect to the Login screen with
   * a flow control flag to prompt the User to login with new credentials, having logged
   * them out
   */
  if (requireFreshLogin) {
    const querystring = `?redirectTo=${encodeURIComponent(Routes.Session.NewCredentials)}`;
    const redirectTo = Routes.Session.Destroy.concat(querystring);

    return <Redirect to={redirectTo} />;
  }

  return (
    <div className="panel box">
      <form className="form" onSubmit={submit}>
        <div className="form-section">
          <Header variant="h2" title="Reset Password" />

          {
            tokenError && (
              <p className="message">
                Invalid or expired password reset link
                <Link className="button primary" to={generatePath(Routes.PasswordReset)}>
                  Resend Email
                </Link>
              </p>
            )
          }

          {!tokenError && (
            <>
              <div className="form-group">
                <TextInput
                  require
                  type="password"
                  label="Password"
                  useFormRegisterReturn={register('password', {
                    required: 'You must enter a new password',
                    minLength: {
                      value: PASSWORD_MIN_LENGTH,
                      message: `Your password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
                    },
                    maxLength: {
                      value: PASSWORD_MAX_LENGTH,
                      message: `Your password cannot be longer than ${PASSWORD_MAX_LENGTH} characters`,
                    },
                  })}
                  error={errors.password?.message}
                />
              </div>

              <div className="form-group">
                <TextInput
                  require
                  type="password"
                  label="Re-enter Password"
                  useFormRegisterReturn={register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === getValues('password') || 'Your password confirmation does not match',
                  })}
                  error={errors.confirmPassword?.message}
                />
              </div>
            </>
          )}
        </div>

        {!tokenError && (
          <div className="form-section form-actions">
            <button className="button primary" type="submit" disabled={isUpdatingPassword}>
              Reset
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

const mapReduxStateToProps = (({ passwordResets }: ApplicationState) => ({
  passwordReset: getPasswordReset(passwordResets),
  onValidateToken: (resetToken: string) => {
    validateToken(resetToken, { id: resetToken, type: 'passwordReset' });
  },
  onSubmit: (password: string, resetToken: string) => {
    updatePassword({
      type: 'passwordReset', password, id: resetToken,
    });
  },
}));

export default connect(mapReduxStateToProps)(ResetPasswordPanel);
